import React, { FunctionComponent } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { textPlaceholder, useSafeIntl } from 'bluesquare-components';
import { Field, FormikProvider, useFormik, useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import moment from 'moment';
import { dateFormat } from '../../../../../../../../hat/assets/js/apps/Iaso/utils/dates';
import { DateInput } from '../../../../components/Inputs';
import MESSAGES from '../../../../constants/messages';
import { CampaignFormValues, Round } from '../../../../constants/types';
import { ReasonForDelayModal } from '../ReasonForDelayModal/ReasonForDelayModal';
import { useRoundDateHistorySchema } from '../ReasonForDelayModal/validation';
import { RoundDatesHistoryModal } from '../RoundDatesHistory/RoundDatesHistoryModal/RoundDatesHistoryModal';

type Props = {
    roundIndex: number;
    roundNumber: number;
    setParentFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
    ) => void;
    parentFieldValue: Round;
};

export const RoundDates: FunctionComponent<Props> = ({
    roundIndex,
    roundNumber,
    setParentFieldValue,
    parentFieldValue,
    // TODO pass parentFieldValue to allow spreading
}) => {
    const { formatMessage } = useSafeIntl();
    const {
        values: { rounds = [] },
        values,
        initialValues,
    } = useFormikContext<CampaignFormValues>();
    const currentStartDate = rounds?.[roundIndex]?.started_at;
    const currentEndDate = rounds?.[roundIndex]?.ended_at;
    // For initial data, wee need to perform a find, because if we're adding round 0
    // We'll be addinga round at the start of the rounds array which will lead to index related errors
    const hasInitialData = Boolean(
        initialValues?.rounds?.find(round => round.number === roundNumber)
            ?.started_at &&
        initialValues?.rounds?.find(round => round.number === roundNumber)
            ?.ended_at,
    );
    const isPlanned =
        values?.is_planned ||
        rounds.some(rnd => rnd.number === roundNumber && rnd.is_planned);
    const save = ({ startDate, endDate, reason_for_delay }, helpers) => {
        setParentFieldValue(`rounds[${roundIndex}]`, {
            ...rounds[roundIndex],
            started_at: startDate,
            ended_at: endDate,
            datelogs: [
                ...(parentFieldValue?.datelogs ?? []),
                {
                    reason_for_delay,
                    previous_started_at: currentStartDate,
                    previous_ended_at: currentEndDate,
                    started_at: startDate,
                    ended_at: endDate,
                },
            ],
        });
        helpers.setSubmitting(false);
    };

    const schema = useRoundDateHistorySchema(rounds, rounds[roundIndex]);

    const formik = useFormik({
        initialValues: {
            startDate: currentStartDate,
            endDate: currentEndDate,
            reason_for_delay: null,
        },
        enableReinitialize: true,
        validateOnBlur: true,
        validationSchema: schema,
        onSubmit: save,
    });
    const isFormChanged =
        !isEqual(formik.values.startDate, formik.initialValues.startDate) ||
        !isEqual(formik.values.endDate, formik.initialValues.endDate);

    const allowConfirm =
        !formik.isSubmitting && formik.isValid && isFormChanged; // && stuff

    return (
        <>
            {(!hasInitialData || isPlanned) && (
                <>
                    <Field
                        label={formatMessage(MESSAGES.startDate)}
                        name={`rounds[${roundIndex}].started_at`}
                        component={DateInput}
                        required
                        fullWidth
                    />

                    <Field
                        label={formatMessage(MESSAGES.endDate)}
                        name={`rounds[${roundIndex}].ended_at`}
                        component={DateInput}
                        required
                        fullWidth
                    />
                </>
            )}
            {hasInitialData && !isPlanned && (
                <FormikProvider value={formik}>
                    <Box mb={2}>
                        <Divider />
                        <Grid container>
                            <Grid item xs={8}>
                                <Box ml={2} mb={2} mt={2}>
                                    <Grid container>
                                        <Grid item xs={4}>
                                            <Typography variant="button">
                                                {`${formatMessage(
                                                    MESSAGES.startDate,
                                                )}: `}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="button">
                                                {`${
                                                    currentStartDate
                                                        ? moment(
                                                              currentStartDate,
                                                          ).format(dateFormat)
                                                        : textPlaceholder
                                                }`}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>

                                <Box ml={2} mb={2}>
                                    <Grid container>
                                        <Grid item xs={4}>
                                            <Typography variant="button">
                                                {`${formatMessage(
                                                    MESSAGES.endDate,
                                                )}: `}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="button">
                                                {`${
                                                    currentEndDate
                                                        ? moment(
                                                              currentEndDate,
                                                          ).format(dateFormat)
                                                        : '--'
                                                }`}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid
                                container
                                item
                                xs={4}
                                alignContent="center"
                                justifyContent="flex-end"
                            >
                                <Box style={{ display: 'flex' }}>
                                    <RoundDatesHistoryModal
                                        roundId={rounds[roundIndex]?.id}
                                        roundNumber={rounds[roundIndex]?.number}
                                        iconProps={{}}
                                    />
                                    <Box
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <ReasonForDelayModal
                                            iconProps={{}}
                                            allowConfirm={allowConfirm}
                                        />
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                        <Divider />
                    </Box>
                </FormikProvider>
            )}
        </>
    );
};
